import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useCommunicatorComps, useCycles, useTeamComps, markCompPresented } from '../../hooks/useFirestore';
import { cn } from '../../utils/cn';
import { normalizeEmail } from '../../utils/hierarchy';
import { CYCLE_STATUSES } from '../../constants';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import AnimatedList from '../../components/ui/AnimatedList';
import { Avatar } from '../../components/ui/StatCard';
import { SkeletonRows } from '../../components/ui/Skeleton';

export default function CommunicatorDashboard() {
  const { user, impersonating, isAdmin, isPeoplePartner } = useAuth();
  const effectiveEmail = impersonating?.email || user?.email;
  const { comps, loading } = useCommunicatorComps(effectiveEmail);
  const { teamComps, loading: teamLoading } = useTeamComps(effectiveEmail);
  const { cycles, loading: cyclesLoading } = useCycles();
  const nav = useNavigate();

  // Only a comp's own communicator (or an admin) may change its status; PPs are read-only
  const canToggle = (comp) => isAdmin
    || (!impersonating && !isPeoplePartner && normalizeEmail(comp.communicatorEmail) === normalizeEmail(effectiveEmail));
  const [search, setSearch] = useState('');
  // The view lives in the URL: "Back" from a statement keeps it, while signing in or a new "View as"
  // lands on plain /communicator, i.e. My People in the current cycle.
  const [params, setParams] = useSearchParams();
  const viewMode = params.get('view') === 'team' ? 'my-team' : 'my-people';
  const showAll = params.get('cycles') === 'all';
  const setParam = useCallback((key, value) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value); else next.delete(key);
    return next;
  }, { replace: true }), [setParams]);
  const setViewMode = (mode) => setParam('view', mode === 'my-team' ? 'team' : null);
  const setShowAll = (all) => setParam('cycles', all ? 'all' : null);

  const [communicatorFilter, setCommunicatorFilter] = useState('all');

  // Drafts are hidden from communicators; admins see them labelled, to preview before launch.
  const visibleCycles = useMemo(
    () => cycles.filter((c) => c.status === 'active' || c.status === 'completed' || (isAdmin && c.status === 'draft')),
    [cycles, isAdmin],
  );
  const cycleById = useMemo(() => new Map(visibleCycles.map((c) => [c.id, c])), [visibleCycles]);
  const currentCycles = useMemo(() => visibleCycles.filter((c) => c.status !== 'completed'), [visibleCycles]);
  const myComps = useMemo(() => comps.filter((c) => cycleById.has(c.cycleId)), [comps, cycleById]);
  const myTeamComps = useMemo(() => teamComps.filter((c) => cycleById.has(c.cycleId)), [teamComps, cycleById]);
  const scoped = useMemo(() => {
    const inScope = (c) => showAll || cycleById.get(c.cycleId).status !== 'completed';
    return { mine: myComps.filter(inScope), team: myTeamComps.filter(inScope) };
  }, [myComps, myTeamComps, cycleById, showAll]);

  const baseComps = viewMode === 'my-team' ? myTeamComps : myComps;
  const scopedComps = viewMode === 'my-team' ? scoped.team : scoped.mine;
  const activeLoading = cyclesLoading || (viewMode === 'my-team' ? teamLoading : loading);
  const hasTeam = myTeamComps.length > 0;

  const teamCommunicators = useMemo(() => {
    const map = new Map();
    scoped.team.forEach((c) => {
      const email = normalizeEmail(c.communicatorEmail);
      if (email) map.set(email, c.communicatorName || email.split('@')[0]);
    });
    return map;
  }, [scoped.team]);

  // Progress per cycle; never summed across cycles
  const progressByCycle = useMemo(() => {
    const map = new Map();
    baseComps.forEach((c) => {
      const p = map.get(c.cycleId) || { total: 0, done: 0, communicators: new Set() };
      p.total += 1;
      if (c._presented) p.done += 1;
      p.communicators.add(normalizeEmail(c.communicatorEmail));
      map.set(c.cycleId, p);
    });
    return map;
  }, [baseComps]);

  // Wait for data before dropping a "My Team" view the user has no team for
  useEffect(() => {
    if (!teamLoading && !cyclesLoading && !hasTeam && viewMode === 'my-team') setParam('view', null);
  }, [teamLoading, cyclesLoading, hasTeam, viewMode, setParam]);

  useEffect(() => {
    setSearch('');
    setCommunicatorFilter('all');
  }, [effectiveEmail]);

  useEffect(() => {
    if (communicatorFilter !== 'all' && !teamCommunicators.has(communicatorFilter)) setCommunicatorFilter('all');
  }, [communicatorFilter, teamCommunicators]);

  const grouped = useMemo(() => {
    let list = scopedComps;
    if (search) {
      const s = search.toLowerCase();
      list = list.filter((c) =>
        c.employeeName?.toLowerCase().includes(s) ||
        c.employeeEmail?.toLowerCase().includes(s) ||
        (viewMode === 'my-team' && c.communicatorName?.toLowerCase().includes(s)),
      );
    }
    if (viewMode === 'my-team' && communicatorFilter !== 'all') {
      list = list.filter((c) => normalizeEmail(c.communicatorEmail) === communicatorFilter);
    }
    const groups = new Map();
    list.forEach((comp) => {
      if (!groups.has(comp.cycleId)) groups.set(comp.cycleId, { cycle: cycleById.get(comp.cycleId), comps: [] });
      groups.get(comp.cycleId).comps.push(comp);
    });
    // Sort each group's comps: uncommunicated first, communicated last
    const result = [...groups.values()];
    result.forEach((g) => g.comps.sort((a, b) => (a._presented ? 1 : 0) - (b._presented ? 1 : 0)));
    return result.sort((a, b) => (b.cycle.createdAt?.seconds || 0) - (a.cycle.createdAt?.seconds || 0));
  }, [scopedComps, search, cycleById, viewMode, communicatorFilter]);

  const currentNames = currentCycles.map((c) => c.name).join(' and ');
  const empty = search || communicatorFilter !== 'all'
    ? { title: 'No matches', body: 'Try a different search or communicator.' }
    : !showAll && currentCycles.length === 0
      ? { title: 'No cycle is active right now', body: 'Past cycles are under All cycles.' }
      : viewMode === 'my-team'
        ? {
            title: showAll ? 'No team assignments yet' : `No one on your team in ${currentNames}`,
            body: "You'll see employees here once compensation packages are assigned to communicators on your team.",
          }
        : {
            title: showAll ? 'No assignments yet' : `No one is assigned to you in ${currentNames}`,
            body: "You'll see employees here once compensation packages are assigned to you.",
          };

  const displayName = impersonating ? impersonating.name : 'My';

  return (
    <div className="space-y-8">
      {/* Hero */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="text-display-sm text-navy-900">
          {viewMode === 'my-team'
            ? `${displayName}${impersonating ? "'s" : ''} Team Overview`
            : impersonating ? `${impersonating.name}'s People` : 'My People'}
        </h1>
        <p className="text-navy-400 mt-1">
          {viewMode === 'my-team'
            ? `Compensation packages across all communicators reporting to ${impersonating ? impersonating.name : 'you'}.`
            : impersonating
              ? `Viewing compensation-change conversations assigned to ${impersonating.name}.`
              : 'Present compensation packages to your team members.'}
        </p>
      </motion.div>

      {/* View mode tabs — only show if user has subordinate communicators */}
      {hasTeam && (
        <div className="flex items-center bg-white rounded-xl border border-surface-200/80 p-0.5 shadow-subtle w-fit">
          <button
            onClick={() => { setViewMode('my-people'); setCommunicatorFilter('all'); }}
            className={cn(
              'px-4 py-1.5 rounded-[10px] text-sm font-semibold transition-all duration-200',
              viewMode === 'my-people' ? 'bg-accent-600 text-white shadow-md shadow-accent-600/20' : 'text-navy-500 hover:text-navy-700',
            )}
          >
            My People
          </button>
          <button
            onClick={() => setViewMode('my-team')}
            className={cn(
              'px-4 py-1.5 rounded-[10px] text-sm font-semibold transition-all duration-200',
              viewMode === 'my-team' ? 'bg-accent-600 text-white shadow-md shadow-accent-600/20' : 'text-navy-500 hover:text-navy-700',
            )}
          >
            My Team
            {teamCommunicators.size > 0 && (
              <span
                className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-white/20 text-[10px] font-bold"
                title={`Communicators on your team in ${showAll ? 'all cycles' : 'the current cycle'}`}
              >
                {teamCommunicators.size}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Glean Comp Companion Agent */}
      <a
        href="https://app.glean.com/chat/agents/a3fc1183e949496aa76c8d7e9bfecb8b"
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 bg-white rounded-2xl border border-surface-200/80 p-4 shadow-subtle hover:shadow-card hover:-translate-y-0.5 transition-all duration-300 w-fit"
      >
        <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center text-white text-lg">💬</span>
        <div>
          <p className="text-sm font-semibold text-navy-900">Ask Comp Companion</p>
          <p className="text-xs text-navy-400">Get help with compensation conversations via Glean</p>
        </div>
        <svg className="w-4 h-4 text-navy-300 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
        </svg>
      </a>

      {/* Cycle filter toggle + Search + Communicator filter */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="flex items-center bg-white rounded-xl border border-surface-200/80 p-0.5 shadow-subtle">
          <button
            onClick={() => setShowAll(false)}
            className={cn(
              'px-4 py-1.5 rounded-[10px] text-sm font-semibold transition-all duration-200',
              !showAll ? 'bg-accent-600 text-white shadow-md shadow-accent-600/20' : 'text-navy-500 hover:text-navy-700',
            )}
          >
            Current cycle
          </button>
          <button
            onClick={() => setShowAll(true)}
            className={cn(
              'px-4 py-1.5 rounded-[10px] text-sm font-semibold transition-all duration-200',
              showAll ? 'bg-accent-600 text-white shadow-md shadow-accent-600/20' : 'text-navy-500 hover:text-navy-700',
            )}
          >
            All cycles
          </button>
        </div>
        {viewMode === 'my-team' && teamCommunicators.size > 0 && (
          <select
            value={communicatorFilter}
            onChange={(e) => setCommunicatorFilter(e.target.value)}
            className="rounded-xl border border-surface-200/80 bg-white px-3 py-2 text-sm text-navy-700 shadow-subtle focus:outline-none focus:ring-2 focus:ring-accent-500/30 focus:border-accent-400"
          >
            <option value="all">All Communicators</option>
            {[...teamCommunicators.entries()]
              .sort(([, a], [, b]) => a.localeCompare(b))
              .map(([email, name]) => (
                <option key={email} value={email}>{name} ({email})</option>
              ))}
          </select>
        )}
        <div className="flex-1 w-full sm:w-auto">
          <Input
            placeholder={viewMode === 'my-team' ? 'Search employees or communicators…' : 'Search employees…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" /></svg>}
          />
        </div>
      </div>

      {/* Current-cycle progress, one row per cycle */}
      {!activeLoading && !showAll && currentCycles.map((cycle) => (
        progressByCycle.has(cycle.id) && (
          <CycleProgress key={cycle.id} cycle={cycle} progress={progressByCycle.get(cycle.id)} showCommunicators={viewMode === 'my-team'} />
        )
      ))}

      {/* Grouped list */}
      {activeLoading ? (
        <SkeletonRows count={4} />
      ) : grouped.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center text-3xl mb-4">📭</div>
          <h3 className="text-lg font-bold text-navy-900 mb-1">{empty.title}</h3>
          <p className="text-sm text-navy-400">{empty.body}</p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(({ cycle, comps: cycleComps }) => {
            const progress = progressByCycle.get(cycle.id);
            return (
              <div key={`${cycle.id}-${viewMode}`}>
                <h2 className="text-lg font-bold text-navy-900 mb-3 flex flex-wrap items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-accent-500" />
                  {cycle.name}
                  <CycleStatusChip status={cycle.status} />
                  {showAll && progress && (
                    <span className="text-xs font-medium text-navy-400">
                      {progress.done}/{progress.total} communicated
                    </span>
                  )}
                </h2>
                {viewMode === 'my-team' ? (
                  <TeamGroupedList comps={cycleComps} nav={nav} canToggle={canToggle} />
                ) : (
                  <AnimatedList className="space-y-2">
                    {cycleComps.map((comp) => (
                      <CompCard key={comp.id} comp={comp} nav={nav} canToggle={canToggle} />
                    ))}
                  </AnimatedList>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Cycle status chip ── */
function CycleStatusChip({ status }) {
  const st = CYCLE_STATUSES[status];
  if (!st) return null;
  return (
    <span className={cn('inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal ring-1 ring-inset', st.color, st.ring)}>
      {status === 'draft' ? 'Draft · only admins can see this' : st.label}
    </span>
  );
}

/* ── Progress tiles for one cycle ── */
function CycleProgress({ cycle, progress, showCommunicators }) {
  const pending = progress.total - progress.done;
  const pct = Math.round((progress.done / progress.total) * 100);
  const tiles = [
    ...(showCommunicators ? [{ label: 'Communicators', value: progress.communicators.size, icon: '📨' }] : []),
    { label: 'Communicated', value: `${progress.done}/${progress.total} (${pct}%)`, icon: '✅' },
    { label: 'Pending', value: `${pending}/${progress.total}`, icon: '⏳' },
  ];
  return (
    <section className="space-y-2">
      <p className="flex flex-wrap items-center gap-2 text-xs font-semibold text-navy-400 uppercase tracking-wider">
        {cycle.name}
        <CycleStatusChip status={cycle.status} />
      </p>
      <div className={cn('grid gap-4', showCommunicators ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2')}>
        {tiles.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.08 }}
            className="bg-white rounded-2xl border border-surface-200/80 p-4 shadow-subtle"
          >
            <div className="text-2xl mb-2">{s.icon}</div>
            <p className="text-xs font-semibold text-navy-400 uppercase tracking-wider">{s.label}</p>
            <p className="text-2xl font-bold text-navy-900 mt-0.5">{s.value}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ── Shared card for a single compensation ── */
function CompCard({ comp, nav, canToggle }) {
  const presented = !!comp._presented;

  const handleToggle = (e) => {
    e.stopPropagation();
    markCompPresented(comp.cycleId, comp.id, !presented).catch(console.error);
  };

  return (
    <Card
      hover
      onClick={() => nav(`/communicator/present/${comp.cycleId}/${comp.id}`)}
      className={cn('flex items-center gap-4 p-4 group', presented && 'opacity-60')}
    >
      {canToggle(comp) && (
        <button
          onClick={handleToggle}
          title={presented ? 'Mark as pending' : 'Mark as communicated'}
          className={cn(
            'w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all',
            presented
              ? 'bg-emerald-500 border-emerald-500 text-white'
              : 'border-surface-300 hover:border-emerald-400 text-transparent hover:text-emerald-300',
          )}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </button>
      )}
      <Avatar name={comp.employeeName} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-navy-900 truncate group-hover:text-accent-700 transition-colors">
          {comp.employeeName || comp.employeeEmail}
        </p>
        <p className="text-xs text-navy-400 truncate">
          {comp.currentTitle}{comp.department ? ` · ${comp.department}` : ''}
        </p>
      </div>
      <Badge category={comp.category} className="hidden sm:inline-flex" />
      {presented && (
        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 hidden sm:inline-flex">
          Communicated
        </span>
      )}
      <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-semibold shadow-md shadow-accent-600/20">
          Present →
        </span>
      </div>
    </Card>
  );
}

/* ── Team view: group comps by communicator with section headers ── */
function TeamGroupedList({ comps, nav, canToggle }) {
  const byCommunicator = useMemo(() => {
    const groups = {};
    comps.forEach((comp) => {
      const key = comp.communicatorEmail?.toLowerCase() || 'unknown';
      if (!groups[key]) groups[key] = { name: comp.communicatorName || key, email: key, comps: [] };
      groups[key].comps.push(comp);
    });
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [comps]);

  return (
    <div className="space-y-5">
      {byCommunicator.map((group) => (
        <div key={group.email}>
          <div className="flex items-center gap-2.5 mb-2 px-1">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
              {(group.name || '?').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
            </div>
            <p className="text-sm font-semibold text-navy-700">{group.name}</p>
            <span className="text-xs text-navy-400">({group.comps.length})</span>
          </div>
          <AnimatedList className="space-y-2">
            {group.comps.map((comp) => (
              <CompCard key={comp.id} comp={comp} nav={nav} canToggle={canToggle} />
            ))}
          </AnimatedList>
        </div>
      ))}
    </div>
  );
}
